import { useCallback, useEffect, useRef, useState } from "react";

import { getErrorMessage, isAPIError } from "@/core/services";
import { useStableValue } from "@/ds";

/**
 * Visão Cards de uma listagem por status (Obras e Financeiro): uma consulta por coluna e "mostrar
 * mais" só da coluna. Cada tela informa como buscar uma página (`fetchColumnPage`) e as mensagens
 * de erro.
 */

/** Espera entre a digitação e a consulta (300ms não cobria quem digita a ~350ms por tecla). */
export const LISTING_SEARCH_DEBOUNCE_MS = 400;

export type ListingQueryStatus = "loading" | "success" | "error" | "forbidden";

export interface ListingSort<TField extends string = string> {
  field: TField;
  direction: "asc" | "desc";
}

export interface ListingMessages {
  forbidden: string;
  error: string;
}

export interface ListingPageRequest<TFilters, TField extends string> {
  busca: string;
  filters: TFilters;
  page: number;
  pageSize: number;
  sort: ListingSort<TField>;
  /** Ausente no "mostrar mais": quem descarta a resposta velha é a geração dos critérios. */
  signal?: AbortSignal;
}

export function describeListingError(
  error: unknown,
  messages: ListingMessages,
): { status: "error" | "forbidden"; errorMessage: string } {
  const isForbidden = isAPIError(error) && error.status === 403;

  return {
    status: isForbidden ? "forbidden" : "error",
    errorMessage: isForbidden ? messages.forbidden : getErrorMessage(error) || messages.error,
  };
}

export interface ListingCardsColumnState<TItem> {
  status: ListingQueryStatus;
  items: TItem[];
  /** Total do status para os critérios em vigor (controla "Mostrar mais obras"). */
  total: number;
  /** Última página já carregada (1-based). */
  page: number;
  isLoadingMore: boolean;
  errorMessage: string | null;
}

/** Cada coluna de cards traz até 10 obras por vez. */
export const LISTING_CARDS_PAGE_SIZE = 10;

export interface UseListingCardsParams<TItem, TFilters, TField extends string> {
  search: string;
  /** Os filtros de status não valem aqui: cada coluna consulta o seu próprio status. */
  filters: TFilters;
  sort: ListingSort<TField>;
  /** Códigos dos status que viram colunas, na ordem de exibição. */
  columns: string[];
  /** Obras por página de cada coluna. Padrão: `LISTING_CARDS_PAGE_SIZE`. */
  pageSize?: number;
  /** Pede uma página de uma coluna: a tela troca o status dos filtros pelo código da coluna. */
  fetchColumnPage: (
    request: ListingPageRequest<TFilters, TField> & { column: string },
  ) => Promise<{ items: TItem[]; total: number }>;
  /** Identidade da obra, para "mostrar mais" não repetir cards já exibidos. */
  getItemId: (item: TItem) => string;
  messages: ListingMessages;
}

/**
 * Visão Cards: uma consulta por coluna de status, `pageSize` 10; "Mostrar mais obras" pede a
 * próxima página só daquela coluna e anexa sem duplicar. Mudar busca, filtro ou ordenação
 * reinicia todas as colunas do começo.
 */
export function useListingCards<TItem, TFilters, TField extends string>({
  search,
  filters,
  sort,
  columns,
  pageSize = LISTING_CARDS_PAGE_SIZE,
  fetchColumnPage,
  getItemId,
  messages,
}: UseListingCardsParams<TItem, TFilters, TField>) {
  const [state, setState] = useState<Record<string, ListingCardsColumnState<TItem>>>({});
  const [reloadToken, setReloadToken] = useState(0);
  const previousTerm = useRef<string | null>(null);
  // Descarta a resposta de "mostrar mais" de uma geração de critérios que já foi substituída.
  const generation = useRef(0);

  const term = search.trim();
  const appliedFilters = useStableValue(filters);
  const columnsKey = columns.join("|");
  const { field: sortField, direction: sortDirection } = sort;

  const latestState = useRef(state);
  latestState.current = state;

  const latest = useRef({ fetchColumnPage, getItemId, messages, pageSize });
  latest.current = { fetchColumnPage, getItemId, messages, pageSize };

  const criteria = useRef({ term, appliedFilters, sortField, sortDirection });
  criteria.current = { term, appliedFilters, sortField, sortDirection };

  useEffect(() => {
    const shouldDebounce = previousTerm.current !== null && previousTerm.current !== term;
    previousTerm.current = term;

    generation.current += 1;
    let cancelled = false;
    const controller = new AbortController();
    const codes = columnsKey === "" ? [] : columnsKey.split("|");

    const loadingColumn: ListingCardsColumnState<TItem> = {
      status: "loading",
      items: [],
      total: 0,
      page: 0,
      isLoadingMore: false,
      errorMessage: null,
    };

    setState(Object.fromEntries(codes.map((code) => [code, loadingColumn])));

    const timer = setTimeout(
      () => {
        for (const code of codes) {
          latest.current
            .fetchColumnPage({
              column: code,
              busca: term,
              filters: appliedFilters,
              page: 1,
              pageSize: latest.current.pageSize,
              sort: { field: sortField, direction: sortDirection },
              signal: controller.signal,
            })
            .then(({ items, total }) => {
              if (cancelled) return;
              setState((current) => ({
                ...current,
                [code]: {
                  status: "success",
                  items,
                  total,
                  page: 1,
                  isLoadingMore: false,
                  errorMessage: null,
                },
              }));
            })
            .catch((error: unknown) => {
              if (cancelled) return;
              setState((current) => ({
                ...current,
                [code]: {
                  ...loadingColumn,
                  ...describeListingError(error, latest.current.messages),
                },
              }));
            });
        }
      },
      shouldDebounce ? LISTING_SEARCH_DEBOUNCE_MS : 0,
    );

    return () => {
      cancelled = true;
      clearTimeout(timer);
      controller.abort();
    };
  }, [term, appliedFilters, sortField, sortDirection, columnsKey, reloadToken]);

  /** Carrega +10 só da coluna, mantendo a ordenação e sem repetir obras já exibidas. */
  const loadMore = useCallback((code: string) => {
    const column = latestState.current[code];
    if (!column || column.isLoadingMore || column.status !== "success") return;

    const startedAt = generation.current;
    const nextPage = column.page + 1;
    const {
      term: busca,
      appliedFilters: filtros,
      sortField: field,
      sortDirection: direction,
    } = criteria.current;

    setState((current) => ({ ...current, [code]: { ...column, isLoadingMore: true } }));

    latest.current
      .fetchColumnPage({
        column: code,
        busca,
        filters: filtros,
        page: nextPage,
        pageSize: latest.current.pageSize,
        sort: { field, direction },
      })
      .then(({ items, total }) => {
        if (generation.current !== startedAt) return;
        setState((current) => {
          const atual = current[code];
          if (!atual) return current;
          const known = new Set(atual.items.map((item) => latest.current.getItemId(item)));
          return {
            ...current,
            [code]: {
              ...atual,
              items: [
                ...atual.items,
                ...items.filter((item) => !known.has(latest.current.getItemId(item))),
              ],
              total,
              page: nextPage,
              isLoadingMore: false,
            },
          };
        });
      })
      .catch(() => {
        if (generation.current !== startedAt) return;
        setState((current) =>
          current[code]
            ? { ...current, [code]: { ...current[code], isLoadingMore: false } }
            : current,
        );
      });
  }, []);

  const reload = useCallback(() => setReloadToken((token) => token + 1), []);

  return { columns: state, loadMore, reload };
}
